/* */

import React, { Fragment, useCallback, useState } from 'react';
import { FormFeedback } from 'reactstrap';
import CreatableSelect from 'react-select/creatable';
import Select from 'components/Select';
import { stylesForRemove } from 'helpers/select';
import { get, isArray, isNumber, isPlainObject, isString } from 'lodash';
import PropTypes from 'prop-types';
import { isDefine, isReactComponent } from 'helpers';
import { cn } from 'helpers/css';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/popover';
import NewDesign from 'components/NewDesign';
import { PlusCircle, XCircle } from 'lucide-react';
import { Badge } from 'shadcn/components/badge';
import { Separator } from 'shadcn/components/separator';
import SelectOptions from 'components/Select/v2/SelectOptions';

const propTypes = {
  placeholder: PropTypes.string,
  label: PropTypes.any,
  afterLabel: PropTypes.any,
  options: PropTypes.array,
  noOptionsMessage: PropTypes.string,
  errorField: PropTypes.string,

  menuPortalTarget: PropTypes.oneOfType([PropTypes.elementType]),

  required: PropTypes.bool,
  cachedOptions: PropTypes.array,
  isLabelComponent: PropTypes.bool,
  isCreatable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isRtl: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMulti: PropTypes.bool,
  isValueObject: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,

  getOptionLabel: PropTypes.func,
  getOptionValue: PropTypes.func,

  afterOnChange: PropTypes.func,
  classNameLabel: PropTypes.string,
  v2: PropTypes.boolean,
  numberOfBadge: PropTypes.number,
  defaultPlaceholder: PropTypes.string,
};

const SelectField = ({
  form,
  field,
  placeholder,
  label,
  note,
  options,

  //! DEPREACATED - on next version react-select, we can use filterOption prop
  // Use for case you want option selected will not display in other selection
  cachedOptions,

  isLabelComponent,
  isCreatable,
  isRtl,
  isSearchable,
  isDisabled,
  isLoading,
  isMulti,
  isValueObject,
  closeMenuOnSelect,

  getOptionLabel,
  getOptionValue,

  onKeyDown,
  onInputChange,
  components,

  menuPortalTarget,
  errorField,

  noOptionsMessage,
  afterOnChange,
  styles = {},
  required = false,
  afterLabel = '',

  onMenuOpen,
  openMenuOnClick,
  onFocus,

  classNameLabel,
  classNameContainer,
  classNameField,
  menuIsOpen,
  filterOption,

  isQuickView,
  isCopyAble,
  isClearable,
  hideCloseIcon,
  onClickQuickView,
  onClickRedirectPage,
  v2,
  numberOfBadge = 2,
  disabled,
  v2Placeholder,
  ...restProps
}) => {
  const [open, setOpen] = useState(false);
  const { name, value = restProps.value } = field || {};
  const { errors, touched, setFieldValue = () => {}, setFieldTouched = () => {} } = form || {};
  let selectOption = value;

  const error = errorField || get(errors || {}, name);
  const touch = get(touched || {}, name);
  const isInvalid = errorField || (touch && !!error);

  //* To prevent confusion between the old and new versions
  const isSelectDisabled = disabled || isDisabled;

  if (isArray(cachedOptions)) {
    if (cachedOptions.find((option) => option.value === value)) {
      selectOption = cachedOptions.find((option) => option.value === value);
    }
  } else {
    if (isArray(options)) {
      //* Is group by label
      if (options.some((el) => 'options' in el)) {
        for (let i = 0; i < options.length; i++) {
          const optionsGrouped = options[i].options;
          const found = optionsGrouped.find((option) => option.value === value);
          if (found) {
            selectOption = found;
            break;
          }
        }
      } else {
        const optionOfValueFound = options.find((option) => option.value === value);
        if (optionOfValueFound) {
          selectOption = optionOfValueFound;
        }
      }
    }
  }

  // If value exists but not found in options, create a custom option to display it
  if (value && !selectOption && (isString(value) || isNumber(value))) {
    selectOption = {
      label: String(value),
      value: value,
      isCustom: true,
    };
  }

  if (isMulti) {
    if (value) {
      if (isString(value)) {
        /** value in multi mode: 'abc, xyz, jqk'
         * split to array of string => ['abc', 'xyz', 'jqk']
         */
        const valueSplit = value.split(',').map((el) => el.toString());

        /** Filter if split array includes value of option
         * E.g:
         * options = [
         *  {
         *    label: 'abc',
         *    value: 'abc
         *  }
         * ]
         *
         * valueSplit = ['abc'];
         *
         */
        selectOption = options.filter((o) => valueSplit.includes(o.value.toString()));

        // Add custom options for values not found in options
        const missingValues = valueSplit.filter(
          (val) => !options.some((opt) => opt.value.toString() === val)
        );
        const customOptions = missingValues.map((val) => ({
          label: val,
          value: val,
          isCustom: true,
        }));
        selectOption = [...selectOption, ...customOptions];
      } else {
        /** If value is object */
        selectOption = value;
      }
    }
  }

  const renderError = useCallback(() => {
    if (errorField) {
      return <FormFeedback>{errorField}</FormFeedback>;
    }

    if (touch) {
      if (!!error && isString(error)) {
        return <FormFeedback>{error}</FormFeedback>;
      }
    }
  }, [error, touch, errorField]);

  const handleSelectChange = async (selectedValue) => {
    if (selectedValue) {
      if (selectedValue?.__isNew__ || isValueObject) {
        await setFieldValue(name, selectedValue);
        afterOnChange(selectedValue);
      } else {
        await setFieldValue(name, selectedValue.value);
        afterOnChange(selectedValue.value);
      }
      return;
    }

    await setFieldValue(name, selectedValue);
    afterOnChange(selectedValue);
  };

  const handleSelectChangeMulti = (listValue, { action, removedValue }) => {
    let valueSelect = '';
    switch (action) {
      case 'remove-value':
      case 'pop-value':
        if (removedValue && removedValue.isFixed) {
          return;
        }
        break;
      case 'clear':
        if (isString(value)) {
          valueSelect = options.filter((v) => v.isFixed);
          setFieldValue(name, valueSelect.map((el) => el.value).join(','));
          afterOnChange(valueSelect.map((el) => el.value).join(',') || '');
          return;
        }

        valueSelect = options.filter((v) => v.isFixed);
        setFieldValue(name, valueSelect);
        afterOnChange(valueSelect || []);
        return;
    }

    if (isString(value)) {
      /**
       * if initialValue = "a,b,c,d"
       * -> convert
       */
      if (listValue) {
        const mapValue = listValue.map((el) => el.value);
        const mapJoin = mapValue.join(',');
        setFieldValue(name, mapJoin);
        afterOnChange(mapJoin || '');
      } else {
        setFieldValue(name, '');
        afterOnChange('');
      }
    } else {
      /**
       * if initial Value = list of object
       */
      if (listValue) {
        setFieldValue(name, listValue);
        afterOnChange(listValue);
      } else {
        setFieldValue(name, []);
        afterOnChange([]);
      }
    }
  };

  const handleBlur = () => {
    setFieldTouched(name, true);
  };

  const handleSelectItemV2 = (option, isSelected) => {
    if (isMulti) {
      let nextSelectOption = selectOption || [];

      if (isSelected) {
        nextSelectOption = nextSelectOption.filter((el) => el.value !== option.value);
      } else {
        nextSelectOption = [...nextSelectOption, option];
      }

      setFieldValue(name, nextSelectOption);
      afterOnChange && afterOnChange(nextSelectOption);
    } else {
      setFieldValue(name, option.value);
      afterOnChange && afterOnChange(option.value);
      setOpen(false);
    }
  };

  const handleCreateNewOption = (newOption) => {
    if (isMulti) {
      let nextSelectOption = selectOption || [];
      nextSelectOption = [...nextSelectOption, newOption];
      setFieldValue(name, nextSelectOption);
      afterOnChange && afterOnChange(nextSelectOption);
    } else {
      setFieldValue(name, newOption.value);
      afterOnChange && afterOnChange(newOption.value);
      setOpen(false);
    }
  };

  if (isCreatable && !v2) {
    return (
      <div className={cn('component:SelectField', classNameContainer)}>
        {label && (
          <label
            className={cn('!tw-mb-1', required ? 'required' : '', classNameLabel)}
            dangerouslySetInnerHTML={{ __html: label }}
          />
        )}
        {note && <p className='tw-text-xs tw-text-gray-500 tw-py-1'>{note}</p>}
        <CreatableSelect
          {...field}
          id={name}
          className={cn('element', {
            'is-invalid-input-select': isInvalid,
          })}
          onChange={handleSelectChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          isClearable={isClearable}
          isLoading={isLoading}
          options={options}
          value={selectOption}
          menuPortalTarget={menuPortalTarget}
          classNamePrefix='react-select'
        />
        {renderError()}
      </div>
    );
  }

  if (v2) {
    const renderValue = () => {
      if (!isDefine(selectOption)) {
        if (v2Placeholder) {
          return (
            <Fragment>
              <Separator orientation='vertical' className='tw-mx-0.5 tw-h-4' />
              {v2Placeholder}
            </Fragment>
          );
        }

        return null;
      }
      return (
        <Fragment>
          <Separator orientation='vertical' className='tw-mx-0.5 tw-h-4' />
          {isArray(selectOption) ? (
            selectOption.length > numberOfBadge ? (
              <Badge variant='secondary' className='tw-rounded-sm tw-px-1 tw-font-extrabold'>
                {selectOption.length} selected
              </Badge>
            ) : (
              selectOption.map((v, index) => {
                return (
                  <Badge
                    key={`${index}`}
                    variant='secondary'
                    className='tw-rounded-sm tw-px-1 tw-font-extrabold tw-gap-1 tw-truncate'
                    title={v?.label}
                  >
                    <p className='tw-max-w-[80px] tw-truncate'>{v?.label}</p>
                  </Badge>
                );
              })
            )
          ) : (
            <Badge variant='secondary' className='tw-rounded-sm tw-px-1 tw-font-extrabold'>
              <p className='tw-max-w-[200px] tw-truncate'>
                {isPlainObject(selectOption) ? selectOption.label : String(selectOption)}
              </p>
            </Badge>
          )}
        </Fragment>
      );
    };

    const shouldShowClearIcon = () => {
      if (!isClearable) {
        return false;
      }

      if (isArray(selectOption) && selectOption.length > 0) {
        return true;
      }

      if ((isString(selectOption) || isNumber(selectOption)) && isDefine(selectOption)) {
        return true;
      }

      if (isPlainObject(selectOption) && isDefine(selectOption?.value)) {
        return true;
      }

      return false;
    };

    return (
      <div className={cn('component:SelectField', classNameContainer)}>
        {note && <p className='tw-text-xs tw-text-gray-400 tw-py-1'>{note}</p>}
        <Popover open={open && !isSelectDisabled} onOpenChange={setOpen}>
          <PopoverTrigger className='tw-w-full tw-min-h-9' onBlur={handleBlur}>
            <NewDesign.Button
              variant='outline'
              className={cn(
                'tw-w-full tw-border-dashed !tw-min-h-9 tw-h-auto tw-py-[6px] tw-justify-start dark:tw-bg-primary-darkInput',
                isInvalid && 'tw-border-red-500 tw-text-red-500',
                isSelectDisabled && 'tw-bg-gray-100 dark:tw-opacity-50 !tw-cursor-default'
              )}
            >
              {shouldShowClearIcon() ? (
                <div
                  role='button'
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();

                    if (isString(selectOption) || isNumber(selectOption)) {
                      setFieldValue(name, '');
                    }

                    if (isPlainObject(selectOption)) {
                      setFieldValue(name, null);
                    }

                    if (isArray(selectOption)) {
                      setFieldValue(name, []);
                    }
                  }}
                  className={cn(
                    'tw-rounded-sm tw-opacity-70 tw-transition-opacity tw-hover:opacity-100 tw-focus-visible:outline-none tw-focus-visible:ring-1 tw-focus-visible:ring-ring',
                    isInvalid && 'tw-text-red-500',
                    'tw-flex tw-items-center'
                  )}
                >
                  <XCircle />
                </div>
              ) : (
                <div className='tw-flex tw-items-center'>
                  <PlusCircle />
                </div>
              )}
              <div className={cn('tw-ml-1', required && 'required')}>{label}</div>

              {renderValue()}
            </NewDesign.Button>

            {renderError()}
          </PopoverTrigger>
          <PopoverContent className='tw-w-max tw-p-0' align='start'>
            <SelectOptions
              options={options}
              isLoading={isLoading}
              isMulti={isMulti}
              selected={selectOption}
              onChangeSelect={handleSelectItemV2}
              onCreateNewOption={handleCreateNewOption}
              isCreatable={isCreatable}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className={cn('component:SelectField', classNameContainer)}>
      {isLabelComponent && label}
      {!isLabelComponent && label && (
        <label
          className={cn('!tw-mb-1', required ? 'required' : '', classNameLabel)}
          dangerouslySetInnerHTML={isReactComponent(label) ? undefined : { __html: label }}
        >
          {isReactComponent(label) ? label : undefined}
        </label>
      )}
      {afterLabel}
      <Select
        {...field}
        components={components}
        id={name}
        className={cn(isInvalid && 'is-invalid-input-select', '!tw-min-h-9', classNameField)}
        onChange={isMulti ? handleSelectChangeMulti : handleSelectChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        options={options}
        value={selectOption}
        isRtl={isRtl}
        isSearchable={isSearchable}
        isDisabled={isSelectDisabled}
        isLoading={isLoading}
        isMulti={isMulti}
        onKeyDown={onKeyDown}
        onInputChange={onInputChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        menuPortalTarget={menuPortalTarget}
        styles={{ ...styles, ...stylesForRemove }}
        noOptionsMessage={noOptionsMessage}
        closeMenuOnSelect={isMulti ? false : closeMenuOnSelect}
        classNamePrefix='react-select'
        onMenuOpen={onMenuOpen}
        onFocus={onFocus}
        menuIsOpen={menuIsOpen}
        filterOption={filterOption}
        hideSelectedOptions={isMulti ? true : false}
        isQuickView={isQuickView}
        isCopyAble={isCopyAble}
        onClickQuickView={onClickQuickView}
        onClickRedirectPage={onClickRedirectPage}
        hideCloseIcon={hideCloseIcon}
        isClearable={isQuickView || isCopyAble || isClearable}
        label={label}
        disabled={disabled}
      />
      {renderError()}
    </div>
  );
};

SelectField.defaultProps = {
  type: 'text',
  onKeyDown: () => {},
  onInputChange: () => {},
  afterOnChange: () => {},
  isValueObject: false,
};

SelectField.propTypes = propTypes;
export default SelectField;
